import { useEffect } from 'react'
import { $rootScope, $state, $stateParams } from '../../../../ng-compat/imports'
import { EVENTS, EVENT } from '../../../../constants'

import { hideAttachCustomerTooltip } from '../../../../state/slices/currentSale/currentSaleSlice'
import { useAppDispatch } from '../../../../state/hooks'
import { STATES } from '../../../../../../lib/routes'

import {
  usePaymentManager,
  useSaleManager,
  useUserManager,
  useVdEscapeStack,
  useWebRegisterStatusManager,
} from '../../../../ng-compat/hooks'
import { usePrintReceipt } from './usePrintReceipt'

type Props = {
  fetchPaymentTypes: () => void // fetch payment types
  updateAmountTendered: () => void // update amount tendered
}
/**
 * This hook is to register & unregister events for PaymentPanel component
 */
export const usePaymentInit = (props: Props) => {
  const dispatch = useAppDispatch()
  const userManager = useUserManager()
  const paymentManager = usePaymentManager()
  const saleManager = useSaleManager()
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const vdEscapeStack = useVdEscapeStack()
  const printReceiptHandler = usePrintReceipt()
  const closePaymentScreen = () => $state.go(STATES.SALE)
  const { fetchPaymentTypes, updateAmountTendered } = props
  /**
   * The dispatch function reference is stable but the React hooks lint rules do not know that
   * Safe to add dispatch to the dependencies array
   * Hide the customer tooltip for lspay quick pay
   */
  useEffect(() => {
    // resolveCustomerGroup not needed so skip it
    if ($stateParams.lspayQuickPaySelected) {
      dispatch(hideAttachCustomerTooltip(true))
    }
  }, [dispatch])

  useEffect(() => {
    const loggedInUserChangedHandler = async () => {
      await Promise.all([
        fetchPaymentTypes(),
        saleManager.updateCustomerBalances(),
      ])
    }

    /**
     * When transitioning online, refresh the customer balances
     * Also update disabled state of Loyalty & Store Credit buttons
     */
    const wrStatusChangeHandler = () => {
      saleManager.updateCustomerBalances()
    }

    /**
     * When the customer is updated or removed from the sale
     * we need to updated the customer related payment options
     * fetchPaymentTypes will trigger re-render on PaymentPanel which will re-run the useCustomerPaymentOptions hook
     * so customer payment options will be updated
     */
    const saleCustomerUpdatedHandler = () => {
      fetchPaymentTypes()
    }
    const saleUpdateTenderedAmount = () => {
      updateAmountTendered()
    }
    userManager.on('loggedInUserChange', loggedInUserChangedHandler)
    paymentManager.on('printReceipt', printReceiptHandler)
    webRegisterStatusManager.on(EVENTS.statusChange, wrStatusChangeHandler)
    const unbindSaleCustomerUpdated = $rootScope.$on(
      EVENT.SALE_CUSTOMER_UPDATED,
      saleCustomerUpdatedHandler
    )
    const unbindSaleUpdateTenderedAmount = $rootScope.$on(
      EVENT.SALE_UPDATE_TENDERED_AMOUNT,
      saleUpdateTenderedAmount
    )
    vdEscapeStack.register(closePaymentScreen)
    $rootScope.$emit('sale:payment')
    return () => {
      userManager.off('loggedInUserChange', loggedInUserChangedHandler)
      paymentManager.off('printReceipt', printReceiptHandler)
      webRegisterStatusManager.off(EVENTS.statusChange, wrStatusChangeHandler)
      unbindSaleCustomerUpdated()
      unbindSaleUpdateTenderedAmount()
      vdEscapeStack.deregister(closePaymentScreen)
    }
    // it is safe to use empty array dependency here because the subsribing / unsubscribing only need to run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
